<!-- BETA-CLEANUP: delete this file and its spec once Vue 3 is the only app -->
<template>
  <v-dialog v-model="isOpen" max-width="440">
    <v-card data-test="beta-intro-dialog" class="beta-intro-dialog">
      <div class="beta-intro-dialog__hero text-primary text-center px-6 pt-8 pb-6">
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close"
          class="beta-intro-dialog__close"
          @click="snooze"
        />
        <v-avatar color="primary" size="56" class="mb-4">
          <BetaRocketIcon :size="32" />
        </v-avatar>
        <div class="d-flex align-center justify-center">
          <span class="text-h6 text-high-emphasis">Try the new SlickCactus</span>
          <CommonChip color="primary" class="ml-2">New</CommonChip>
        </div>
        <p class="text-body-2 text-medium-emphasis mt-2 mb-0">
          A fresh design for the app you use every day.
        </p>
        <p v-if="releaseNote" class="text-body-2 font-weight-medium mt-2 mb-0" data-test="beta-intro-release">
          {{ releaseNote }}
        </p>
      </div>
      <v-list density="compact" class="px-4 py-3">
        <v-list-item v-for="point in points" :key="point.text" :prepend-gap="16">
          <template #prepend>
            <v-icon color="primary">{{ point.icon }}</v-icon>
          </template>
          <v-list-item-title class="text-body-2">{{ point.text }}</v-list-item-title>
        </v-list-item>
      </v-list>
      <div class="px-6 pb-6">
        <v-btn
          color="primary"
          variant="flat"
          block
          size="large"
          append-icon="mdi-arrow-right"
          :loading="isOpeningBeta"
          data-test="beta-intro-try"
          data-track="beta.intro-dialog.open"
          @click="open"
        >
          Try the new version
        </v-btn>
        <v-btn
          variant="text"
          block
          class="mt-2"
          data-test="beta-intro-later"
          @click="snooze"
        >
          Not now
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import { getBetaReleaseNote, openBeta, snoozeBetaIntro } from '@/bundles/App/helpers/betaChannel';

import BetaRocketIcon from '@/bundles/App/components/BetaRocketIcon.vue';
import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';

interface IBetaIntroPoint {
  icon: string;
  text: string;
}

const points: IBetaIntroPoint[] = [
  { icon: 'mdi-database-check-outline', text: 'Same data, nothing to set up' },
  { icon: 'mdi-account-check-outline', text: 'You stay signed in on the same page' },
  { icon: 'mdi-swap-horizontal', text: 'Opens for you from now on; switch back any time' },
];

const isOpen = ref(true);
const releaseNote = getBetaReleaseNote();
const isOpeningBeta = ref(false);

// closing by Esc or a click outside counts as "Not now"
watch(isOpen, (value) => {
  if (!value) snoozeBetaIntro();
});

const snooze = (): void => {
  isOpen.value = false;
};

const open = async (): Promise<void> => {
  isOpeningBeta.value = true;

  try {
    await openBeta();
  } finally {
    isOpeningBeta.value = false;
  }
};
</script>

<style lang="scss" scoped>
// a light tint of the primary colour behind the header, readable in both themes
.beta-intro-dialog__hero {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: currentColor;
    opacity: 0.08;
    pointer-events: none;
  }
}

.beta-intro-dialog__close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}
</style>
